import { Contract } from "@/types/contract";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Printer, Download } from "lucide-react";

interface DealSummaryProps {
  contract: Contract;
  onClose: () => void;
}

export function DealSummary({ contract, onClose }: DealSummaryProps) {
  const handlePrint = () => {
    window.print();
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatPercentage = (value?: string | number) => {
    if (!value) return 'N/A';
    if (typeof value === 'string') {
      return value.includes('%') ? value : `${value}%`;
    }
    return `${value}%`;
  };

  return (
    <div className="max-w-4xl mx-auto p-8 bg-white">
      {/* Print controls - hidden when printing */}
      <div className="flex justify-between items-center mb-6 print:hidden">
        <h1 className="text-2xl font-bold">Investment Deal Summary</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePrint}>
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>

      {/* Summary content */}
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center border-b pb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{contract.title}</h1>
          <p className="text-lg text-gray-600">{contract.property_address || 'Property Address Not Specified'}</p>
          <p className="text-sm text-gray-500 mt-2">Prepared on {new Date().toLocaleDateString()}</p>
        </div>

        {/* Investment Overview */}
        <Card>
          <CardHeader>
            <CardTitle>Investment Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold mb-3">Financial Highlights</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Asking Price:</span>
                    <span className="font-semibold">{formatCurrency(contract.asking_price)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Current Balance:</span>
                    <span className="font-semibold">{formatCurrency(contract.current_balance)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Monthly Payment:</span>
                    <span className="font-semibold">{formatCurrency(contract.monthly_payment)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Property Value:</span>
                    <span className="font-semibold">{formatCurrency(contract.property_value)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Expected Yield:</span>
                    <span className="font-semibold text-green-600">{formatPercentage(contract.investor_yield)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Investment-to-Value:</span>
                    <span className="font-semibold">{formatPercentage(contract.investment_to_value)}</span>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold mb-3">Contract Details</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Remaining Balance:</span>
                    <span className="font-semibold">{formatCurrency(contract.remaining_balance)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Term Remaining:</span>
                    <span className="font-semibold">{contract.term_remaining || 'N/A'}</span>
                  </div>
                  {contract.has_balloon_payment && (
                    <>
                      <div className="flex justify-between">
                        <span>Balloon Payment:</span>
                        <span className="font-semibold">{formatCurrency(contract.balloon_payment_amount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Balloon Due Date:</span>
                        <span className="font-semibold">
                          {contract.balloon_payment_due_date ? new Date(contract.balloon_payment_due_date).toLocaleDateString() : 'N/A'}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Original Contract Terms */}
        {(contract.original_sales_price || contract.original_down_payment) && (
          <Card>
            <CardHeader>
              <CardTitle>Original Contract Terms</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6 text-sm">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Original Sales Price:</span>
                    <span className="font-semibold">{formatCurrency(contract.original_sales_price)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Down Payment:</span>
                    <span className="font-semibold">{formatCurrency(contract.original_down_payment)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Interest Rate:</span>
                    <span className="font-semibold">{contract.original_interest_rate ? `${contract.original_interest_rate}%` : 'N/A'}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Original Payment:</span>
                    <span className="font-semibold">{formatCurrency(contract.original_monthly_payment)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Original Term:</span>
                    <span className="font-semibold">{contract.original_term ? `${contract.original_term} months` : 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sales Date:</span>
                    <span className="font-semibold">
                      {contract.sales_date ? new Date(contract.sales_date).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Due Diligence Information */}
        <Card>
          <CardHeader>
            <CardTitle>Due Diligence Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 text-sm">
              <div>
                <h5 className="font-semibold mb-2">Payment History</h5>
                <p className="text-gray-700">
                  {contract.buyer_payment_history_summary || 'Payment history information not available'}
                </p>
              </div>
              <div>
                <h5 className="font-semibold mb-2">Credit Assessment</h5>
                <p className="text-gray-700">
                  {contract.buyer_credit_score_summary || 'Credit information not available'}
                </p>
              </div>
              <div>
                <h5 className="font-semibold mb-2">Collateral Details</h5>
                <p className="text-gray-700">
                  {contract.collateral_details || 'Collateral details not available'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>


        {/* Additional Notes */}
        {contract.notes && (
          <Card>
            <CardHeader>
              <CardTitle>Additional Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{contract.notes}</p>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center text-xs text-gray-500 border-t pt-4">
          <p>This deal summary is confidential and intended for qualified investors only.</p>
          <p>Generated on {new Date().toLocaleString()}</p>
        </div>
      </div>
    </div>
  );
}